import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { supabase } from '../lib/supabase.js';
import { authFetch } from '../lib/authFetch.js';
import { warmUpScanner, isWeakFormat } from '../lib/zxing.js';
import { startCamera, stopCamera, runScanLoop, setTorch, torchSupported, grabStill } from '../lib/scan-camera.js';
import { unlockAudio, beepOk, beepUnknown, haptic, acquireWakeLock, releaseWakeLock, tryLockPortrait, unlockOrientation } from '../lib/scan-feedback.js';
import {
  lookupBarcode, cartLineFrom, uid, buildPayload, buildTitle, saveLocal, loadLocal, clearLocal,
} from '../lib/scan-session.js';
import { shouldAccept } from '../lib/scan-rule.js';
import { TZ, thToday, thAddDays } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง

const IMG = u => (!u ? '' : (/^https?:/.test(u) ? u : `https://nwfovqicnyxjobkbjcva.supabase.co/storage/v1/object/public/zort-products/${u}`));
const fmtDT = d => (d ? new Date(d).toLocaleString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// หน้า "ยิงบาร์โค้ด" — ใช้บนมือถือ (เพิ่มไปยังหน้าจอโฮมแล้วเปิดเต็มจอ)
// ยิงรัว → ได้รายการ → บันทึกเป็นร่าง → ไปกรอกต้นทุน/สร้างใบซื้อต่อที่หน้า "ซื้อ" บนคอม
const todayISO = thToday;      // YYYY-MM-DD ตามวันไทย (เดิมตามเวลาเครื่อง)
const addDaysISO = thAddDays;
// s = 'YYYY-MM-DD' → เที่ยงคืนไทย (+07:00) แล้วแสดงแบบไทย — ไม่ขึ้นกับ timezone เครื่อง
const fmtDate = s => (s ? new Date(s + 'T00:00:00+07:00').toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', year: '2-digit' }) : '');

export default function Scan({ onExit }) {
  const { profile, hasCostAccess } = useAuth();
  const [view, setView] = useState('home');       // 'home' | 'scanning'
  const [cart, setCart] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [flash, setFlash] = useState(null);       // { id, kind, text, sub }
  const [pending, setPending] = useState(null);   // ชีตสินค้าที่ยังไม่มีใน PD
  const [picker, setPicker] = useState(null);     // ชีตเลือกเมื่อเจอหลายตัว
  const [manual, setManual] = useState(null);     // ชีตค้นหา/พิมพ์เอง { q, rows, searching }
  const [finish, setFinish] = useState(null);     // ชีตจบรายการซื้อ { expected_at }
  const [torchOn, setTorchOn] = useState(false);
  const [torchOk, setTorchOk] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const videoRef = useRef(null);
  const camRef = useRef(null);
  const loopStopRef = useRef(null);
  const lastAcceptRef = useRef({ text: '', at: 0 });
  const seenRef = useRef({ text: '', at: 0 });   // ผลอ่านล่าสุด (ไม่ว่าจะรับหรือไม่) = ใช้ดูว่าป้ายหลุดเฟรมไปหรือยัง
  const confirmRef = useRef({ text: '', at: 0 });
  const inFlightRef = useRef(false);
  const baseRef = useRef(null);
  const retriedRef = useRef(false);
  const flashTimer = useRef(null);
  const watchdogRef = useRef(null);
  // กระจกเงาของ state สำหรับอ่านในลูปกล้อง (ลูปจับ closure ไว้ตั้งแต่ตอนเริ่ม state จะค้างของเก่า)
  const cartRef = useRef([]);
  const unknownRef = useRef([]);
  const sheetRef = useRef(false);
  const decodeRef = useRef(() => {});

  const total = cart.reduce((t, l) => t + (Number(l.qty) || 0), 0) + unknown.reduce((t, u) => t + (Number(u.qty) || 0), 0);
  const empty = !cart.length && !unknown.length;
  const totalTHB = cart.reduce((t, l) => t + (Number(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0);
  // จบใบจากมือถือได้เฉพาะซื้อเติมของที่มีใน PD ครบทุกตัว (ตัวที่ยังไม่รู้จักต้องไปสร้างที่คอมก่อน)
  // และ /api/pd/quick กันด้วย role จัดซื้อ/ผู้จัดการ/บัญชี — กันไว้ฝั่งหน้าจอด้วยจะได้ไม่เจอ 403 ตอนกด
  const canFinish = hasCostAccess && cart.length > 0 && unknown.length === 0;

  /* ---------- teardown: ฟังก์ชันสำคัญที่สุดในหน้านี้ ---------- */
  const teardown = useCallback(() => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    try { loopStopRef.current?.(); } catch { /* ignore */ }
    loopStopRef.current = null;
    stopCamera(camRef.current?.stream, videoRef.current);
    camRef.current = null;
    setTorchOn(false);
    setTorchOk(false);
    releaseWakeLock();
    unlockOrientation();
    setView('home');
  }, []);

  useEffect(() => {
    document.body.classList.add('scan-mode');
    warmUpScanner().catch(e => setErr('โหลดตัวอ่านบาร์โค้ดไม่สำเร็จ: ' + (e?.message ?? e)));
    const saved = loadLocal();
    if (saved && (saved.cart?.length || saved.unknown?.length)) {
      setCart(saved.cart || []);
      setUnknown(saved.unknown || []);
      setSupplier(saved.supplier || '');
      setNote(saved.note || '');
      setDraftId(saved.draftId || null);
      baseRef.current = saved.base || null;
    }
    loadDrafts();
    return () => { document.body.classList.remove('scan-mode'); teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เซฟลง localStorage ทุกครั้งที่รายการเปลี่ยน + sync กระจกเงาให้ลูปอ่าน
  useEffect(() => {
    cartRef.current = cart;
    unknownRef.current = unknown;
    saveLocal({ cart, unknown, supplier, note, draftId, base: baseRef.current });
  }, [cart, unknown, supplier, note, draftId]);

  useEffect(() => { sheetRef.current = !!(pending || picker || manual || finish); }, [pending, picker, manual, finish]);

  useEffect(() => {
    const off = () => teardown();
    const onVis = () => { if (document.visibilityState === 'hidden') teardown(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', off);
    window.addEventListener('hashchange', off);  // WebKit 215884: hash เปลี่ยนขณะกล้องเปิด = ถูกถามสิทธิ์ใหม่
    window.addEventListener('popstate', off);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', off);
      window.removeEventListener('hashchange', off);
      window.removeEventListener('popstate', off);
    };
  }, [teardown]);

  async function loadDrafts() {
    try { const o = await authFetch('/api/pd/drafts', { method: 'GET' }); setDrafts(o.drafts || []); }
    catch { /* เงียบ — ไม่ใช่เรื่องคอขาดบาดตาย */ }
  }

  function showFlash(kind, text, sub) {
    clearTimeout(flashTimer.current);
    setFlash({ id: Date.now() + Math.random(), kind, text, sub });
    flashTimer.current = setTimeout(() => setFlash(null), 950);
  }

  /* ---------- เปิดกล้อง (ต้องอยู่ใน gesture ที่ผู้ใช้กด) ---------- */
  async function begin() {
    setErr(''); setMsg('');
    unlockAudio();                 // gesture เดียวกับ getUserMedia ไม่งั้นเสียงค้าง suspended
    setView('scanning');
    try {
      await warmUpScanner();
      const el = videoRef.current;
      if (!el) throw new Error('ยังเปิดหน้ากล้องไม่สำเร็จ');
      const cam = await startCamera(el);
      camRef.current = cam;
      setTorchOk(torchSupported(cam.track));
      acquireWakeLock();
      tryLockPortrait();   // ได้ผลบน Android — iOS ต้องล็อกจาก Control Center เอง (แต่หน้านี้อ่านป้ายตะแคงได้อยู่แล้ว)
      loopStopRef.current = runScanLoop(el, (t, f) => decodeRef.current(t, f));
      // WebKit 252465: ใน PWA <video> ค้างที่ suspend ได้ — ลองใหม่ครั้งเดียว ห้ามวนซ้ำ
      watchdogRef.current = setTimeout(() => {
        const v = videoRef.current;
        if (!camRef.current || !v) return;
        if (v.readyState < 2 || v.videoWidth === 0) {
          if (retriedRef.current) { teardown(); setErr('กล้องไม่ตอบสนอง — ปิดแอปแล้วเปิดใหม่'); return; }
          retriedRef.current = true;
          teardown();
          begin();
        }
      }, 3000);
    } catch (e) {
      teardown();
      setErr(e?.name === 'NotAllowedError'
        ? 'ยังไม่ได้อนุญาตให้ใช้กล้อง — เปิดสิทธิ์กล้องให้เว็บนี้ในตั้งค่า แล้วลองใหม่'
        : 'เปิดกล้องไม่ได้: ' + (e?.message ?? e));
    }
  }

  async function toggleTorch() {
    const t = camRef.current?.track;
    const next = !torchOn;
    const ok = await setTorch(t, next);
    if (ok) setTorchOn(next);
  }

  /* ---------- รับผลอ่าน: กันซ้ำ + บวกจำนวนอัตโนมัติ ---------- */
  function onDecoded(text, format) {
    const now = performance.now();
    const ok = shouldAccept({ seen: seenRef.current, accepted: lastAcceptRef.current }, text, now);
    seenRef.current = { text, at: now };   // ต้องอัปเดตทุกครั้งที่อ่านได้ แม้ครั้งนี้จะไม่รับ
    if (!ok || sheetRef.current || inFlightRef.current) return;

    if (isWeakFormat(format)) {   // ITF/Code39 ไม่มี check digit — ต้องอ่านตรงกัน 2 ครั้ง
      const p = confirmRef.current;
      if (p.text !== text || now - p.at > 600) { confirmRef.current = { text, at: now }; return; }
      confirmRef.current = { text: '', at: 0 };
    }

    lastAcceptRef.current = { text, at: now };
    accept(text, format);
  }
  decodeRef.current = onDecoded;

  async function accept(text, format) {
    // 1) มีในตะกร้ารอบนี้แล้ว → +1 ทันที ไม่ยิงเน็ต
    const line = cartRef.current.find(l => l.barcode === text || l.sku === text);
    if (line) {
      const next = Number(line.qty || 0) + 1;
      setCart(c => c.map(x => (x.sku === line.sku ? { ...x, qty: Number(x.qty || 0) + 1 } : x)));
      beepOk(); haptic(); showFlash('ok', line.name, `+1 → ${next}`);
      return;
    }
    // 2) เป็นตัวที่ยังไม่รู้จักซึ่งเพิ่งบันทึกไปรอบนี้ → +1
    const un = unknownRef.current.find(u => u.barcode === text);
    if (un) {
      const next = Number(un.qty || 0) + 1;
      setUnknown(u => u.map(x => (x.uid === un.uid ? { ...x, qty: Number(x.qty || 0) + 1 } : x)));
      beepOk(); haptic(); showFlash('ok', 'ยังไม่รู้จัก · ' + text, `+1 → ${next}`);
      return;
    }
    // 3) โค้ดใหม่ → ค้น PD
    inFlightRef.current = true;
    try {
      const r = await lookupBarcode(text);
      if (r.status === 'hit') {
        addProduct(r.hit);
      } else if (r.status === 'ambiguous' || r.status === 'inactive') {
        beepUnknown();
        setPicker({ barcode: text, rows: r.rows, inactive: r.status === 'inactive' });
      } else {
        beepUnknown();
        setPending({ uid: uid(), barcode: text, format: format || '', code: '', price: '', qty: 1, note: '', photoBlob: null, photoPreview: null, photo_url: null });
      }
    } catch (e) {
      beepUnknown();
      showFlash('err', 'ค้นหาไม่สำเร็จ', e.message);
      lastAcceptRef.current = { text: '', at: 0 };  // ให้ยิงซ้ำตัวเดิมได้ทันที
    } finally {
      inFlightRef.current = false;
    }
  }

  function addProduct(p) {
    let bumped = 0;
    setCart(c => {
      const i = c.findIndex(x => x.sku === p.sku);
      if (i >= 0) { bumped = Number(c[i].qty || 0) + 1; return c.map((x, k) => (k === i ? { ...x, qty: bumped } : x)); }
      return [...c, cartLineFrom(p)];
    });
    beepOk(); haptic();
    showFlash('ok', p.name, bumped ? `+1 → ${bumped}` : p.sku);
  }

  /* ---------- ชีตสินค้าที่ยังไม่มีใน PD ---------- */
  async function takePhoto() {
    const blob = await grabStill(videoRef.current);
    if (!blob) { setErr('ถ่ายรูปไม่สำเร็จ — กล้องยังไม่พร้อม'); return; }
    setPending(p => (p ? { ...p, photoBlob: blob, photoPreview: URL.createObjectURL(blob) } : p));
  }

  async function uploadPhoto(blob, barcode) {
    const path = `scan/${String(barcode).replace(/[^A-Za-z0-9_-]/g, '') || 'x'}-${Date.now()}.jpg`;
    const { error } = await supabase.storage.from('sourcepoint-media')
      .upload(path, blob, { contentType: 'image/jpeg', upsert: false });
    if (error) throw error;
    return supabase.storage.from('sourcepoint-media').getPublicUrl(path).data.publicUrl;
  }

  async function savePending() {
    const p = pending;
    if (!p) return;
    if (!p.code.trim() && !String(p.price).trim()) { setErr('ใส่รหัสสินค้าหรือราคาขายอย่างน้อยหนึ่งอย่าง'); return; }
    setBusy(true); setErr('');
    let photo_url = p.photo_url || null;
    if (p.photoBlob) {
      // แนบรูปไม่สำเร็จต้องไม่บล็อกการบันทึกรายการ
      try { photo_url = await uploadPhoto(p.photoBlob, p.barcode); }
      catch (e) { setMsg('แนบรูปไม่สำเร็จ (' + (e.message ?? e) + ') — บันทึกรายการให้แล้ว'); }
    }
    const item = {
      uid: p.uid, barcode: p.barcode, format: p.format,
      code: p.code.trim(), price: String(p.price).trim(), qty: Number(p.qty) || 1,
      note: p.note.trim(), photo_url, scanned_at: new Date().toISOString(),
    };
    setUnknown(u => (u.some(x => x.uid === item.uid) ? u.map(x => (x.uid === item.uid ? item : x)) : [...u, item]));
    if (p.photoPreview) URL.revokeObjectURL(p.photoPreview);
    setPending(null);
    setBusy(false);
    showFlash('ok', 'บันทึกไว้แล้ว', p.barcode);
  }

  function cancelPending() {
    if (pending?.photoPreview) URL.revokeObjectURL(pending.photoPreview);
    setPending(null);
    lastAcceptRef.current = { text: '', at: 0 };
  }

  /* ---------- บันทึกเป็นร่าง ---------- */
  async function save() {
    if (busy) return;
    if (empty) { setErr('ยังไม่มีรายการ'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      teardown();  // ปิดกล้องก่อนยิงเน็ต
      const payload = buildPayload({ supplier, note, cart, unknown, base: baseRef.current });
      const title = buildTitle(cart, unknown);
      const o = await authFetch('/api/pd/drafts', {
        method: 'POST',
        body: JSON.stringify({ id: draftId || undefined, title, payload }),
      });
      // ฝั่ง PD ไม่เช็คว่าแถวยังอยู่ไหมตอน update — ถ้าคอมลบร่างนี้ไปแล้ว จะได้ ok กลับมาแต่ไม่ได้เขียนอะไร
      if (draftId && o) {
        try {
          const list = await authFetch('/api/pd/drafts', { method: 'GET' });
          if (!(list.drafts || []).some(d => d.id === draftId)) {
            await authFetch('/api/pd/drafts', { method: 'POST', body: JSON.stringify({ title, payload }) });
          }
        } catch { /* เช็คไม่ได้ก็ปล่อย — ของหลักบันทึกไปแล้ว */ }
      }
      clearLocal();
      setCart([]); setUnknown([]); setSupplier(''); setNote(''); setDraftId(null); baseRef.current = null;
      setMsg('บันทึกร่างแล้ว — เปิดต่อที่หน้า “ใบสั่งซื้อ” บนคอมได้เลย');
      loadDrafts();
    } catch (e) {
      setErr(e.message);  // ของยังอยู่ครบใน localStorage กดใหม่ได้
    } finally { setBusy(false); }
  }

  /* ---------- ค้นหาด้วย SKU / ชื่อสินค้า (ไม่ต้องยิงกล้อง) ---------- */
  const searchTimer = useRef(null);
  const manualInputRef = useRef(null);

  // ล้างช่อง + คืนโฟกัส เพื่อให้ยิงปืนบาร์โค้ดรัวต่อได้ทันที (ปืนที่ Purim ใช้ไม่ส่ง Enter มาให้)
  function resetManualInput(bump) {
    setManual(m => (m ? { ...m, q: '', rows: null, searching: false, added: (m.added || 0) + (bump ? 1 : 0) } : m));
    setTimeout(() => manualInputRef.current?.focus(), 0);
  }

  // เจอตัวเดียวจากบาร์โค้ด/SKU ตรงเป๊ะ = +1 ให้เลย แล้วล้างช่องรอตัวถัดไป (ค้นด้วยชื่อยังต้องกดเลือกเอง)
  // lookupSeq กันบวกซ้ำ: ปืนบางรุ่นส่ง Enter ตามมาระหว่างที่ตัวค้นจากดีบาวซ์ยังวิ่งอยู่ → ผลเก่าตกไป
  const lookupSeq = useRef(0);
  async function runManualLookup(s) {
    const seq = ++lookupSeq.current;
    try {
      const o = await authFetch(`/api/pd/search?q=${encodeURIComponent(s)}`, { method: 'GET' });
      if (seq !== lookupSeq.current) return;              // มีการยิงรอบใหม่แซงแล้ว ทิ้งผลนี้
      const rows = o.rows || [];
      if (o.matched_by === 'barcode' && rows.length === 1) { addProduct(rows[0]); resetManualInput(true); return; }
      setManual(m => (m ? { ...m, rows, searching: false } : m));
    } catch (e) {
      if (seq !== lookupSeq.current) return;
      setManual(m => (m ? { ...m, rows: [], searching: false } : m));
      setErr(e.message);
    }
  }

  function onSearchChange(q) {
    setManual(m => ({ ...m, q }));
    clearTimeout(searchTimer.current);
    const s = q.trim();
    if (s.length < 2) { setManual(m => ({ ...m, rows: null, searching: false })); return; }
    setManual(m => ({ ...m, searching: true }));
    // ปืนพ่นตัวเลขเร็วมาก — รหัสล้วนตัวเลขรอสั้นลงให้ +1 ทันใจ ส่วนคนพิมพ์ชื่อคงไว้ 350ms
    searchTimer.current = setTimeout(() => runManualLookup(s), /^[0-9]{6,}$/.test(s) ? 180 : 350);
  }

  // ปืนบางรุ่นส่ง Enter/Tab ท้ายรหัส — ยิงค้นทันทีไม่ต้องรอ debounce
  function onManualKeyDown(e) {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    e.preventDefault();
    const s = (manual?.q ?? '').trim();
    if (s.length < 2) return;
    clearTimeout(searchTimer.current);
    if (manual?.rows?.length === 1) { lookupSeq.current++; addProduct(manual.rows[0]); resetManualInput(true); return; }
    setManual(m => ({ ...m, searching: true }));
    runManualLookup(s);
  }

  function pickFromSearch(p) {
    addProduct(p);
    resetManualInput(true);            // ไม่ปิดชีต — ยิงตัวถัดไปต่อได้เลย
    lastAcceptRef.current = { text: '', at: 0 };
    seenRef.current = { text: '', at: 0 };
  }

  /* ---------- จบรายการซื้อจากมือถือเลย (restock ของที่มีใน PD อยู่แล้ว) ---------- */
  async function createBuy() {
    if (busy || !finish) return;
    const lines = cart
      .map(l => ({ sku: l.sku, name: l.name, qty: Number(l.qty) || 0, unit_cost_thb: parseFloat(l.unit_cost) || 0 }))
      .filter(l => l.sku && l.qty > 0);
    if (!lines.length) { setErr('ยังไม่มีรายการ'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      teardown();
      await authFetch('/api/pd/quick', {
        method: 'POST',
        body: JSON.stringify({ op: 'create', lines, supplier, note, expected_at: finish.expected_at || undefined }),
      });
      if (draftId) { try { await authFetch('/api/pd/drafts', { method: 'DELETE', body: JSON.stringify({ id: draftId }) }); } catch { /* ร่างค้างไว้ไม่เป็นไร */ } }
      clearLocal();
      setCart([]); setUnknown([]); setSupplier(''); setNote(''); setDraftId(null); baseRef.current = null;
      setFinish(null);
      setMsg(`สร้างใบซื้อแล้ว — ขึ้นเป็น “สินค้ากำลังเข้า” ใน PD ทันที${finish.expected_at ? ` · ของถึง ${fmtDate(finish.expected_at)}` : ''} · รอกดรับเข้าคลังตอนของมาถึง`);
      loadDrafts();
    } catch (e) {
      setErr(e.message);   // ของยังอยู่ครบใน localStorage
    } finally { setBusy(false); }
  }

  function loadDraft(d) {
    const p = d.payload || {};
    baseRef.current = p;
    setSupplier(p.supplier || ''); setNote(p.note || '');
    setCart(Array.isArray(p.cart) ? p.cart : []);
    setUnknown(Array.isArray(p.unknown) ? p.unknown : []);
    setDraftId(d.id); setErr(''); setMsg('เปิดร่าง “' + (d.title || '') + '” มาต่อแล้ว');
  }

  function clearSession() {
    if (!window.confirm('ล้างรายการที่ยิงไว้ทั้งหมด?')) return;
    clearLocal();
    setCart([]); setUnknown([]); setSupplier(''); setNote(''); setDraftId(null); baseRef.current = null;
    setMsg(''); setErr('');
  }

  const bump = (sku, d) => setCart(c => c.map(x => (x.sku === sku ? { ...x, qty: Math.max(0, Number(x.qty || 0) + d) } : x)).filter(x => Number(x.qty) > 0));
  const bumpU = (u2, d) => setUnknown(u => u.map(x => (x.uid === u2 ? { ...x, qty: Math.max(0, Number(x.qty || 0) + d) } : x)).filter(x => Number(x.qty) > 0));

  if (profile && !profile.is_active) {
    return (
      <div className="sc-root"><div className="sc-page sc-safe">
        <div className="eyebrow">ยิงบาร์โค้ด · Scan</div>
        <h1 className="sc-h1">สแกนสินค้า</h1>
        <p className="sub">บัญชีนี้ถูกปิดใช้งาน — ติดต่อผู้จัดการ</p>
      </div></div>
    );
  }

  return (
    <div className="sc-root">
      {/* กล้องอยู่ใน DOM ตลอด เพื่อให้ videoRef พร้อมใช้ตอนกดปุ่ม (ซ่อนไว้ตอนอยู่หน้าแรก) */}
      <div className={`sc-stage ${view === 'scanning' ? '' : 'hide'}`}>
        <video ref={videoRef} playsInline muted autoPlay />
        <div className="sc-aim" />
        <div className="sc-hint">เล็งให้บาร์โค้ดอยู่ในกรอบ · ห่างประมาณ 10–15 ซม. · ป้ายวางตะแคงก็อ่านได้ ไม่ต้องเอียงเครื่อง</div>
        {flash && (
          <div key={flash.id} className={`sc-flash ${flash.kind}`}>
            <div><b>{flash.text}</b>{flash.sub ? <span>{flash.sub}</span> : null}</div>
          </div>
        )}
      </div>

      {view === 'scanning' ? (
        <>
          <div className="sc-side">
          <div className="sc-list">
            {empty ? <div className="sc-none">ยังไม่มีรายการ — เล็งบาร์โค้ดได้เลย</div> : null}
            {[...unknown].reverse().map(u => (
              <div className="sc-row unknown" key={u.uid}>
                <div className="sc-main">
                  <div className="sc-nm">ยังไม่รู้จัก · {u.code || (u.price ? `฿${u.price}` : 'ไม่มีข้อมูล')}</div>
                  <div className="sc-sub">{u.barcode}</div>
                </div>
                <div className="sc-step">
                  <button onClick={() => bumpU(u.uid, -1)} aria-label="ลด">−</button>
                  <span className="sc-qty">{u.qty}</span>
                  <button onClick={() => bumpU(u.uid, 1)} aria-label="เพิ่ม">+</button>
                </div>
              </div>
            ))}
            {[...cart].reverse().map(l => (
              <div className="sc-row" key={l.sku}>
                <div className="sc-main">
                  <div className="sc-nm">{l.name}</div>
                  <div className="sc-sub">{l.sku}</div>
                </div>
                <div className="sc-step">
                  <button onClick={() => bump(l.sku, -1)} aria-label="ลด">−</button>
                  <span className="sc-qty">{l.qty}</span>
                  <button onClick={() => bump(l.sku, 1)} aria-label="เพิ่ม">+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="sc-bar sc-safe-b">
            {torchOk && (
              <button className={`sc-btn ${torchOn ? 'on' : ''}`} onClick={toggleTorch} aria-label="ไฟฉาย">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 3L5 14h6l-1 7 8-11h-6l1-7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
              </button>
            )}
            <button className="sc-btn" onClick={() => { setFinish(null); setManual({ q: '', rows: null, searching: false, added: 0 }); }}>ค้นหา / พิมพ์เอง</button>
            <div className="sc-count">{total} ชิ้น</div>
            <button className="sc-btn primary" onClick={teardown}>เสร็จ</button>
          </div>
          </div>
        </>
      ) : (
        <div className="sc-page sc-safe">
          <div className="sc-top">
            <div className="eyebrow">ยิงบาร์โค้ด · Scan</div>
            {onExit && <button className="sc-link" onClick={onExit}>ไปหน้าจัดซื้อ →</button>}
          </div>
          <h1 className="sc-h1">{empty ? 'ยิงบาร์โค้ด\nทำรายการซื้อ' : `รายการนี้ ${total} ชิ้น`}</h1>

          <button className="sc-btn primary sc-start" onClick={begin}>
            {empty ? 'เริ่มรายการซื้อใหม่' : 'สแกนต่อ'}
          </button>
          <button className="sc-btn sc-wide sc-add" onClick={() => { setFinish(null); setManual({ q: '', rows: null, searching: false, added: 0 }); }}>
            + เพิ่มด้วยการพิมพ์ SKU / ชื่อสินค้า
          </button>

          {err && <p className="err-note">{err}</p>}
          {msg && <div className="cost-note" style={{ borderColor: 'var(--ink)', marginTop: 14 }}><span>{msg}</span></div>}

          {!empty && (
            <>
              <div className="sc-fields">
                <div className="field">
                  <div className="lbl"><span className="th">ซัพพลายเออร์ / ที่มา</span><span className="opt">ไม่บังคับ</span></div>
                  <input className="under" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="ใส่ทีหลังที่คอมก็ได้" />
                </div>
                <div className="field">
                  <div className="lbl"><span className="th">หมายเหตุ</span><span className="opt">ไม่บังคับ</span></div>
                  <input className="under" value={note} onChange={e => setNote(e.target.value)} placeholder="เช่น เลขบิล / รอบซื้อ" />
                </div>
              </div>

              <div className="sc-list open">
                {unknown.map(u => (
                  <div className="sc-row unknown" key={u.uid}>
                    <div className="sc-main" onClick={() => setPending({ ...u, price: u.price ?? '', photoBlob: null, photoPreview: null })}>
                      <div className="sc-nm">ยังไม่รู้จัก · {u.code || (u.price ? `฿${u.price}` : 'ไม่มีข้อมูล')}</div>
                      <div className="sc-sub">{u.barcode}{u.photo_url ? ' · มีรูป' : ''}</div>
                    </div>
                    <div className="sc-step">
                      <button onClick={() => bumpU(u.uid, -1)} aria-label="ลด">−</button>
                      <span className="sc-qty">{u.qty}</span>
                      <button onClick={() => bumpU(u.uid, 1)} aria-label="เพิ่ม">+</button>
                    </div>
                  </div>
                ))}
                {cart.map(l => (
                  <div className="sc-row" key={l.sku}>
                    {l.image_url ? <img className="sc-th" src={IMG(l.image_url)} alt="" onError={e => { e.target.style.opacity = .15; }} /> : <div className="sc-th empty" />}
                    <div className="sc-main">
                      <div className="sc-nm">{l.name}</div>
                      <div className="sc-sub">{l.sku}</div>
                    </div>
                    <div className="sc-step">
                      <button onClick={() => bump(l.sku, -1)} aria-label="ลด">−</button>
                      <span className="sc-qty">{l.qty}</span>
                      <button onClick={() => bump(l.sku, 1)} aria-label="เพิ่ม">+</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="sc-actions">
                <button className="sc-btn" onClick={clearSession}>ล้างรายการ</button>
                <button className="sc-btn" onClick={save} disabled={busy}>{busy ? 'กำลังบันทึก…' : 'บันทึกร่าง'}</button>
              </div>

              {/* ซื้อเติมของที่มีใน PD อยู่แล้ว → จบใบได้เลยจากมือถือ ไม่ต้องไปเปิดคอม */}
              {canFinish ? (
                <>
                  <button className="sc-btn primary sc-wide sc-finish" onClick={() => { setManual(null); setFinish({ expected_at: '' }); }} disabled={busy}>
                    จบรายการซื้อเลย · รอรับเข้าคลัง
                  </button>
                  <p className="sc-foot">
                    ของจะขึ้นเป็น <b>สินค้ากำลังเข้า</b> ใน PD ทันที (เห็นที่หน้าสินค้า + รายงานขายล่วงหน้า) แล้วรอกดรับเข้าคลังตอนของมาถึง
                    · หรือกด “บันทึกร่าง” ถ้าอยากไปแก้ต้นทุน/จำนวนที่คอมก่อน
                  </p>
                </>
              ) : (
                <p className="sc-foot">
                  {unknown.length
                    ? `มีสินค้าที่ยังไม่รู้จัก ${unknown.length} ตัว — จบใบจากมือถือไม่ได้ ต้องบันทึกร่างแล้วไปสร้างสินค้าที่คอมก่อน`
                    : !hasCostAccess
                      ? 'บัญชีนี้จบใบซื้อเองไม่ได้ (ต้องเป็น จัดซื้อ / ผู้จัดการ / บัญชี) — บันทึกร่างไว้ให้คนที่มีสิทธิ์ทำต่อได้'
                      : 'บันทึกแล้วไปเปิดต่อที่หน้า “ใบสั่งซื้อ” บนคอม — ใส่ต้นทุน/จำนวนแล้วค่อยสร้างใบซื้อ'}
                </p>
              )}
            </>
          )}

          {empty && (
            <div className="sc-drafts">
              <h5>ร่างล่าสุด</h5>
              {drafts.length === 0
                ? <p className="sd-empty">ยังไม่มีร่าง</p>
                : drafts.slice(0, 8).map(d => (
                  <button className="sc-draft" key={d.id} onClick={() => loadDraft(d)}>
                    <span className="sc-dt">{d.title || '(ไม่มีชื่อ)'}</span>
                    <span className="sc-dd">{fmtDT(d.updated_at)}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- ชีต: สินค้าที่ยังไม่มีใน PD ---------- */}
      {pending && (
        <>
          <div className="sc-scrim" onClick={cancelPending} />
          <div className="sc-sheet">
            <h3>ยังไม่มีในระบบ</h3>
            <div className="sc-bc mono">{pending.barcode}</div>
            <p className="sc-note">ใส่แค่อย่างเดียวก็พอ — <b>รหัสบนป้าย</b> หรือ <b>ราคาขาย</b> ไว้ไปตามที่คอมทีหลัง</p>

            <div className="field">
              <div className="lbl"><span className="th">รหัสสินค้า / โค้ดบนป้าย</span></div>
              <input className="under mono" value={pending.code} onChange={e => setPending(p => ({ ...p, code: e.target.value }))} placeholder="เช่น TP-262" />
            </div>
            <div className="fx-row">
              <div className="field">
                <div className="lbl"><span className="th">ราคาขาย ฿</span></div>
                <input className="under mono" inputMode="decimal" value={pending.price} onChange={e => setPending(p => ({ ...p, price: e.target.value.replace(/[^0-9.]/g, '') }))} placeholder="0" />
              </div>
              <div className="field">
                <div className="lbl"><span className="th">จำนวน</span></div>
                <input className="under mono" inputMode="numeric" value={pending.qty} onChange={e => setPending(p => ({ ...p, qty: e.target.value.replace(/[^0-9]/g, '') }))} />
              </div>
            </div>
            <div className="field">
              <div className="lbl"><span className="th">หมายเหตุ</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under" value={pending.note} onChange={e => setPending(p => ({ ...p, note: e.target.value }))} placeholder="เช่น สีเขียว ไซซ์ M" />
            </div>

            {camRef.current && (
              pending.photoPreview || pending.photo_url
                ? <div className="sc-photo"><img src={pending.photoPreview || pending.photo_url} alt="" /><button className="sc-btn" onClick={takePhoto}>ถ่ายใหม่</button></div>
                : <button className="sc-btn sc-wide" onClick={takePhoto}>📷 ถ่ายรูปป้าย (ไม่บังคับ)</button>
            )}

            {err && <p className="err-note">{err}</p>}
            <div className="sc-sheet-act">
              <button className="sc-btn" onClick={cancelPending}>ข้ามตัวนี้</button>
              <button className="sc-btn primary" onClick={savePending} disabled={busy || (!pending.code.trim() && !String(pending.price).trim())}>
                {busy ? 'กำลังบันทึก…' : 'เพิ่มเข้ารายการ'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ---------- ชีต: เจอหลายตัว / ตัวที่ปิดใช้งาน ---------- */}
      {picker && (
        <>
          <div className="sc-scrim" onClick={() => { setPicker(null); lastAcceptRef.current = { text: '', at: 0 }; }} />
          <div className="sc-sheet">
            <h3>{picker.inactive ? 'สินค้าถูกปิดใช้งาน' : 'เจอหลายตัว — เลือกอันที่ใช่'}</h3>
            <div className="sc-bc mono">{picker.barcode}</div>
            {picker.rows.map(p => (
              <button className="sc-pick" key={p.sku} onClick={() => { addProduct(p); setPicker(null); }}>
                <span className="sc-nm">{p.name}</span>
                <span className="sc-sub mono">{p.sku}{p.is_active === false ? ' · ปิดใช้งาน' : ''}</span>
              </button>
            ))}
            <div className="sc-sheet-act">
              <button className="sc-btn" onClick={() => { setPicker(null); lastAcceptRef.current = { text: '', at: 0 }; }}>ปิด</button>
              <button className="sc-btn" onClick={() => { const b = picker.barcode; setPicker(null); setPending({ uid: uid(), barcode: b, format: '', code: '', price: '', qty: 1, note: '', photoBlob: null, photoPreview: null, photo_url: null }); }}>
                บันทึกเป็นตัวใหม่
              </button>
            </div>
          </div>
        </>
      )}

      {/* ---------- ชีต: ค้นหา SKU / ชื่อสินค้า / พิมพ์บาร์โค้ดเอง ---------- */}
      {manual && (
        <>
          <div className="sc-scrim" onClick={() => setManual(null)} />
          <div className="sc-sheet">
            <h3>ยิงปืนบาร์โค้ด / พิมพ์เอง</h3>
            <p className="sc-note">
              <b>ยิงปืนรัวได้เลย</b> — เจอในระบบจะ <b>+1 ให้เอง</b> แล้วล้างช่องรอตัวถัดไป ·
              หรือพิมพ์ <b>SKU / ชื่อสินค้า</b> แล้วเลือกจากรายการ
            </p>
            <div className="field">
              <input className="under" autoFocus ref={manualInputRef} value={manual.q}
                     onChange={e => onSearchChange(e.target.value)}
                     onKeyDown={onManualKeyDown}
                     placeholder="ยิงบาร์โค้ด / SKU / ชื่อสินค้า" />
            </div>
            {manual.added > 0 && (
              <p className="sc-note" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                ✓ เพิ่มแล้ว {manual.added} ครั้งในรอบนี้ — ยิงตัวถัดไปได้เลย
              </p>
            )}

            {manual.searching && <p className="sd-empty">กำลังค้น…</p>}

            {manual.rows?.length > 0 && (
              <div className="sc-results">
                {manual.rows.map(p => (
                  <button className="sc-pick" key={p.sku} onClick={() => pickFromSearch(p)}>
                    {p.image_url ? <img className="sc-th" src={IMG(p.image_url)} alt="" onError={e => { e.target.style.opacity = .15; }} /> : <div className="sc-th empty" />}
                    <span className="sc-main">
                      <span className="sc-nm">{p.name}</span>
                      <span className="sc-sub">{p.sku}{p.on_hand != null ? ` · คงเหลือ ${p.on_hand}` : ''}</span>
                    </span>
                    <span className="sc-plus">{cart.find(x => x.sku === p.sku) ? `✓ ×${cart.find(x => x.sku === p.sku).qty}` : '+'}</span>
                  </button>
                ))}
              </div>
            )}

            {manual.rows?.length === 0 && !manual.searching && (
              <p className="sd-empty">ไม่พบใน PD ตรงกับ “{manual.q}”{/^[0-9]{6,}$/.test(manual.q.trim()) ? ' — กด “บันทึกเป็นตัวใหม่” ถ้าเป็นสินค้าใหม่' : ''}</p>
            )}

            <div className="sc-sheet-act">
              <button className="sc-btn" onClick={() => setManual(null)}>ปิด</button>
              {/^[0-9]{6,}$/.test(manual.q.trim()) && manual.rows?.length === 0 && (
                <button className="sc-btn" onClick={() => { const c = manual.q.trim(); setManual(null); setPending({ uid: uid(), barcode: c, format: 'manual', code: '', price: '', qty: 1, note: '', photoBlob: null, photoPreview: null, photo_url: null }); }}>
                  บันทึกเป็นตัวใหม่
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ---------- ชีต: จบรายการซื้อ (ซื้อเติมของที่มีใน PD แล้ว) ---------- */}
      {finish && (
        <>
          <div className="sc-scrim" onClick={() => !busy && setFinish(null)} />
          <div className="sc-sheet">
            <h3>จบรายการซื้อ</h3>
            <p className="sc-note">
              {cart.length} รายการ · {total} ชิ้น{totalTHB > 0 ? ` · รวม ฿${totalTHB.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : ''}
              {supplier ? ` · ${supplier}` : ''}
            </p>

            <div className="field">
              <div className="lbl"><span className="th">วันที่ของจะถึงคลัง</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under mono" type="date" value={finish.expected_at}
                     onChange={e => setFinish(f => ({ ...f, expected_at: e.target.value }))} />
              <div className="sc-chips">
                {[['วันนี้', 0], ['พรุ่งนี้', 1], ['+3 วัน', 3], ['+7 วัน', 7], ['+14 วัน', 14]].map(([label, n]) => (
                  <button key={label} className={`sc-chip ${finish.expected_at === (n ? addDaysISO(n) : todayISO()) ? 'on' : ''}`}
                          onClick={() => setFinish(f => ({ ...f, expected_at: n ? addDaysISO(n) : todayISO() }))}>{label}</button>
                ))}
                {finish.expected_at && <button className="sc-chip" onClick={() => setFinish(f => ({ ...f, expected_at: '' }))}>ล้าง</button>}
              </div>
            </div>

            <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
              <span>ยืนยันแล้วของจะขึ้นเป็น <b>สินค้ากำลังเข้า</b> ใน PD ทันที (ยังไม่เพิ่มสต๊อกจริง) · ตอนของมาถึงค่อยเปิดใบแล้วกด <b>รับของเข้าคลัง</b> ที่หน้า “ใบสั่งซื้อ” บนคอม สต๊อกถึงจะเข้า Free zone สาขา 71</span>
            </div>

            {err && <p className="err-note">{err}</p>}
            <div className="sc-sheet-act">
              <button className="sc-btn" onClick={() => setFinish(null)} disabled={busy}>ยกเลิก</button>
              <button className="sc-btn primary" onClick={createBuy} disabled={busy}>{busy ? 'กำลังสร้าง…' : 'ยืนยันสร้างใบซื้อ'}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
